import { PrinterOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Button,
  Form,
  InputNumber,
  Modal,
  Radio,
  Select,
  Space,
  Spin,
  Typography,
} from 'antd';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  getLabelsListPresetsQueryKey,
  labelsPrintLabels,
  useLabelsListPresets,
} from '@/api/generated/endpoints/labels/labels';
import type { LabelPresetOut, LabelsIn } from '@/api/generated/model';
import { ShareFileModal } from '@/components/ShareFileModal';
import { problemMessage } from '@/i18n/problem';
import { shareFirst } from '@/lib/share';
import { useIsMobile } from '@/lib/useIsMobile';
import { publicBase } from '@/runtime';

const OTHER = 'other';
const MIN_MM = 30;
const MAX_MM = 300;

export interface LabelTarget {
  id: number;
  pallets: number;
  product: string;
}

interface Values {
  format: 'a4' | 'sticker';
  preset: string; // «58x40» или OTHER
  width_mm: number | null;
  height_mm: number | null;
  pallets: number[];
}

const presetKey = (p: Pick<LabelPresetOut, 'width_mm' | 'height_mm'>) =>
  `${p.width_mm}x${p.height_mm}`;

/** Размер стикера: из сохранённого («58x40») или введённый («Другой размер…»). */
function stickerSize(v: Values): Pick<LabelsIn, 'width_mm' | 'height_mm'> {
  if (v.preset === OTHER) return { width_mm: v.width_mm, height_mm: v.height_mm };
  const [w, h] = v.preset.split('x').map(Number);
  return { width_mm: w ?? null, height_mm: h ?? null };
}

/** Начальные значения: последний формат печати (ADR-0011 п. 4). */
function defaults(presets: LabelPresetOut[]): Values {
  const last = presets[0];
  const sticker = presets.find((p) => p.kind === 'sticker');
  return {
    format: last?.kind ?? 'a4',
    preset: sticker ? presetKey(sticker) : OTHER,
    width_mm: sticker?.width_mm ?? 58,
    height_mm: sticker?.height_mm ?? 40,
    pallets: [],
  };
}

/** Открыть PDF для печати. Окно открываем сразу по клику — иначе браузер заблокирует. */
function openPdf(win: Window | null, blob: Blob, name: string) {
  const url = URL.createObjectURL(blob);
  if (win) {
    win.location.href = url;
  } else {
    const a = document.createElement('a');
    a.href = url;
    a.download = name;
    a.click();
  }
  setTimeout(() => {
    URL.revokeObjectURL(url);
  }, 60_000);
}

/** Печать этикеток палетов партии: A4 или стикер своего размера; выбор палетов. */
export function LabelPrintModal({ batch, onClose }: { batch: LabelTarget; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form] = Form.useForm<Values>();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  // Телефон: сначала сформировать, потом отдельным нажатием — «Поделиться» (Safari разрешает
  // меню только прямо по нажатию, а после ожидания сервера разрешение уже истекло).
  const share = shareFirst(useIsMobile());
  const [ready, setReady] = useState<File | null>(null);
  const presets = useLabelsListPresets();
  const format = Form.useWatch('format', form);
  const preset = Form.useWatch('preset', form);
  const stickers = (presets.data ?? []).filter((p) => p.kind === 'sticker');

  async function submit(v: Values) {
    setError(null);
    const win = share ? null : window.open('', '_blank');
    setBusy(true);
    try {
      const body: LabelsIn = {
        format: v.format,
        // Поле размера есть в форме только у стикера: у A4 значения preset нет вовсе
        // (на новом сервере A4 по умолчанию — разбор preset падал, T-205).
        ...(v.format === 'sticker' ? stickerSize(v) : {}),
        pallets: v.pallets.length ? v.pallets : null,
        link_base: publicBase(),
      };
      const pdf = await labelsPrintLabels(batch.id, body, { responseType: 'blob' });
      if (!(pdf instanceof Blob)) throw new Error(t('labels.notPdf'));
      const name = t('labels.fileName', { id: batch.id });
      await queryClient.invalidateQueries({ queryKey: getLabelsListPresetsQueryKey() });
      if (share) {
        setReady(new File([pdf], name, { type: 'application/pdf' }));
        return;
      }
      openPdf(win, pdf, name);
      onClose();
    } catch (e) {
      win?.close();
      setError(problemMessage(e));
    } finally {
      setBusy(false);
    }
  }

  if (ready) {
    return (
      <ShareFileModal
        title={t('labels.title', { id: batch.id })}
        file={ready}
        sendText={t('labels.share')}
        hint={t('labels.shareHint')}
        onClose={onClose}
      />
    );
  }

  const palletOptions = Array.from({ length: batch.pallets }, (_, i) => ({
    value: i + 1,
    label: t('labels.palletNo', { no: i + 1, total: batch.pallets }),
  }));

  return (
    <Modal
      open
      title={t('labels.title', { id: batch.id })}
      onCancel={onClose}
      footer={[
        <Button key="cancel" onClick={onClose}>
          {t('common.cancel')}
        </Button>,
        <Button
          key="print"
          type="primary"
          icon={<PrinterOutlined />}
          loading={busy}
          disabled={!presets.data}
          onClick={() => {
            form.submit();
          }}
        >
          {share ? t('labels.make') : t('labels.print')}
        </Button>,
      ]}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">
        {t('labels.hint', { product: batch.product, count: batch.pallets })}
      </Typography.Paragraph>
      {/* Форма — только когда форматы загружены: иначе их приход сбрасывал уже сделанный
          выбор (нажали «Стикер» — через мгновение снова A4). После печати список
          перезапрашивается — initialValues выбор не трогают. */}
      {!presets.data ? (
        <Spin />
      ) : (
        <Form<Values>
          form={form}
          layout="vertical"
          initialValues={defaults(presets.data)}
          onFinish={(v) => void submit(v)}
        >
          <Form.Item name="format" label={t('labels.format')}>
            <Radio.Group
              optionType="button"
              options={[
                { value: 'a4', label: t('labels.a4') },
                { value: 'sticker', label: t('labels.sticker') },
              ]}
            />
          </Form.Item>
          {format === 'sticker' && (
            <>
              <Form.Item name="preset" label={t('labels.size')}>
                <Select
                  options={[
                    ...stickers.map((p) => ({
                      value: presetKey(p),
                      label: t('labels.sizeMm', { w: p.width_mm, h: p.height_mm }),
                    })),
                    { value: OTHER, label: t('labels.otherSize') },
                  ]}
                />
              </Form.Item>
              {preset === OTHER && (
                <Space align="start">
                  <Form.Item
                    name="width_mm"
                    label={t('labels.width')}
                    rules={[{ required: true, message: t('labels.sizeRequired') }]}
                  >
                    <InputNumber min={MIN_MM} max={MAX_MM} precision={0} suffix={t('labels.mm')} />
                  </Form.Item>
                  <Form.Item
                    name="height_mm"
                    label={t('labels.height')}
                    rules={[{ required: true, message: t('labels.sizeRequired') }]}
                  >
                    <InputNumber min={MIN_MM} max={MAX_MM} precision={0} suffix={t('labels.mm')} />
                  </Form.Item>
                </Space>
              )}
            </>
          )}
          <Form.Item name="pallets" label={t('labels.pallets')} extra={t('labels.palletsHint')}>
            <Select
              mode="multiple"
              allowClear
              placeholder={t('labels.allPallets', { count: batch.pallets })}
              options={palletOptions}
            />
          </Form.Item>
        </Form>
      )}
      {error && <Alert type="error" showIcon message={error} />}
    </Modal>
  );
}
